import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, TrendingDown, DollarSign, Sparkles, RefreshCw, CreditCard } from "lucide-react";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Stat = ({ label, value, sub, accent, icon: Icon, testid }) => (
  <div className="card" style={accent ? { borderColor: "#FF0055" } : {}} data-testid={testid}>
    <p className="eyebrow flex items-center gap-1.5" style={accent ? { color: "#FF0055" } : {}}>{Icon && <Icon size={11} strokeWidth={2.5} />} {label}</p>
    <p className="font-display font-black text-3xl md:text-4xl mt-2" style={accent ? { color: "#FF0055" } : {}}>{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const STATUS_COLOR = { active: "#FF0055", cancelled: "#71717a", suspended: "#f59e0b", pending: "#a1a1aa", expired: "#52525b", admin: "#ffffff" };

export default function AdminPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("revenue");

  const load = () => api.get("/admin/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin") return <div className="p-10 text-zinc-500">Admin only.</div>;
  if (!d) return <div className="p-10 text-zinc-500">Loading…</div>;

  const { members, revenue, churn, engagement, monthly, members_list } = d;
  const chartKey = { revenue: "revenue", members: "new_members", churn: "cancellations" }[tab];

  return (
    <div className="fade-up" data-testid="admin-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow" style={{ color: "#FF0055" }}>Admin · {revenue.mode === "live" ? "Live" : "Sandbox"} numbers</p>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-[0.95] mt-2">MEMBER <span style={{ color: "#FF0055" }}>NUMBERS</span></h1>
        </div>
        <div className="flex gap-2">
          <Link to="/profile" className="btn-secondary flex items-center gap-2" data-testid="admin-paypal-link"><CreditCard size={14} /> PayPal setup</Link>
          <button className="btn-ghost flex items-center gap-2" onClick={load} data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-8 stagger">
        <Stat label="Active members" value={members.active} sub={`${members.paying} paying incl. ${members.cancelled_retaining} finishing period`} accent icon={Users} testid="stat-active" />
        <Stat label={`MRR (${revenue.currency})`} value={money(revenue.mrr)} sub={`${members.active} × ${money(revenue.price)}`} icon={DollarSign} testid="stat-mrr" />
        <Stat label="Churn (30d)" value={`${churn.churn_rate_30d}%`} sub={`${churn.cancelled_30d} cancelled · ${churn.new_members_30d} new`} icon={TrendingDown} testid="stat-churn" />
        <Stat label={`Collected this month (${revenue.currency})`} value={money(revenue.collected_this_month)} sub={`Last month ${money(revenue.collected_last_month)} · ${revenue.payments_total} payments total`} testid="stat-collected" />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4 stagger">
        <Stat label="Total accounts" value={members.total_users} sub={`${churn.new_users_7d} new this week · ${churn.new_users_30d} this month`} testid="stat-users" />
        <Stat label="Conversion" value={`${churn.conversion_rate}%`} sub="paying ÷ non-admin accounts" testid="stat-conversion" />
        <Stat label="Check-ins this week" value={engagement.checkins_this_week} sub={`${engagement.journal_7d} proof entries · 7d`} icon={Sparkles} testid="stat-checkins" />
        <Stat label="Workouts (7d)" value={engagement.workouts_7d} sub={`${engagement.coach_msgs_7d} coach msgs · ${engagement.habits_total} habits tracked`} testid="stat-workouts" />
      </section>

      <section className="card mt-8" data-testid="admin-chart">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="eyebrow">Last 6 months</p>
          <div className="flex gap-1">
            {[["revenue", "Revenue"], ["members", "New members"], ["churn", "Cancellations"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`chart-tab-${k}`}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors"
                style={{ border: `1px solid ${tab === k ? "#FF0055" : "#3f3f46"}`, background: tab === k ? "#FF0055" : "transparent", color: tab === k ? "white" : "#a1a1aa" }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} itemStyle={{ color: "#FF0055" }}
                formatter={(v) => (tab === "revenue" ? `${money(v)} ${revenue.currency}` : v)} />
              <Bar dataKey={chartKey} radius={0}>
                {monthly.map((m, i) => <Cell key={m.key} fill={i === monthly.length - 1 ? "#FF0055" : "#3f3f46"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="mt-8" data-testid="admin-members">
        <p className="eyebrow">Members</p>
        <h2 className="font-display font-black text-2xl md:text-3xl mt-1">THE ROSTER <span style={{ color: "#FF0055" }}>({members_list.length})</span></h2>
        <div className="card p-0 mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid #27272A" }}>
                {["Member", "Status", "Since", "Next billing", "Cancelled"].map((h) => <th key={h} className="eyebrow font-bold px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {members_list.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-zinc-500 text-center" data-testid="admin-members-empty">No members yet. Share the membership page — the first one is the hardest.</td></tr>
              )}
              {members_list.map((m) => (
                <tr key={m.id} style={{ borderBottom: "1px solid #1f1f22" }} data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3"><p className="font-display font-bold uppercase text-xs">{m.name || "—"}</p><p className="text-xs text-zinc-500">{m.email}</p></td>
                  <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1" style={{ border: `1px solid ${STATUS_COLOR[m.status] || "#3f3f46"}`, color: STATUS_COLOR[m.status] || "#a1a1aa" }}>{m.status}</span></td>
                  <td className="px-4 py-3 text-zinc-300 text-xs">{fmt(m.since)}</td>
                  <td className="px-4 py-3 text-zinc-300 text-xs">{fmt(m.next_billing_time)}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{fmt(m.cancelled_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
